import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Fetches a representative product image for each subcategory name.
 * Queries the product_categories table by subcategory field,
 * then fetches the corresponding product image.
 * Returns a Map<subcategoryName, imageUrl>.
 */
async function fetchSubcategoryImages(subcategoryNames: string[]): Promise<Map<string, string>> {
  if (subcategoryNames.length === 0) return new Map();

  const result = new Map<string, string>();

  // 1. Query product_categories by subcategory name to find product_ids
  const { data: subMappings } = await supabase
    .from("product_categories")
    .select("product_id, subcategory")
    .in("subcategory", subcategoryNames);

  // 2. Also check products.category field (some subcategory names may also be used as primary category)
  const remaining = subcategoryNames.filter(
    n => !(subMappings || []).some(m => m.subcategory === n)
  );

  let primaryProducts: { category: string; image_url: string; images: string[] }[] = [];
  if (remaining.length > 0) {
    const { data } = await supabase
      .from("products")
      .select("category, image_url, images")
      .eq("is_published", true)
      .in("category", remaining)
      .order("sold_count", { ascending: false })
      .limit(200);
    primaryProducts = data || [];
  }

  // Add primary product images
  for (const p of primaryProducts) {
    if (result.has(p.category)) continue;
    const img = getValidImage(p.image_url, p.images);
    if (img) result.set(p.category, img);
  }

  // 3. Collect unique product IDs from subcategory mappings
  const productIds = [...new Set((subMappings || []).map(m => m.product_id))];
  if (productIds.length === 0) return result;

  // 4. Fetch product images in batches
  let products: { id: string; image_url: string; images: string[] }[] = [];
  for (let i = 0; i < productIds.length; i += 50) {
    const { data } = await supabase
      .from("products")
      .select("id, image_url, images")
      .eq("is_published", true)
      .in("id", productIds.slice(i, i + 50));
    products = products.concat(data || []);
  }

  const productMap = new Map(products.map(p => [p.id, p]));

  // 5. Map subcategory name → first valid product image
  for (const m of subMappings || []) {
    if (!m.subcategory || result.has(m.subcategory)) continue;
    const prod = productMap.get(m.product_id);
    if (prod) {
      const img = getValidImage(prod.image_url, prod.images);
      if (img) result.set(m.subcategory, img);
    }
  }

  return result;
}

function getValidImage(imageUrl: string, images?: string[]): string | null {
  if (imageUrl && imageUrl.startsWith("http")) return imageUrl;
  if (images && images.length > 0 && images[0].startsWith("http")) return images[0];
  return null;
}

export function useSubcategoryImages(subcategoryNames: string[]) {
  return useQuery({
    queryKey: ["subcategory-images", subcategoryNames.join(",")],
    queryFn: async () => {
      const map = await fetchSubcategoryImages(subcategoryNames);
      // Prefetch all images into browser cache
      if (map.size > 0) {
        for (const url of map.values()) {
          const link = document.createElement("link");
          link.rel = "prefetch";
          link.as = "image";
          link.href = url;
          document.head.appendChild(link);
        }
      }
      return map;
    },
    staleTime: 5 * 60 * 1000,
    enabled: subcategoryNames.length > 0,
  });
}
